/**
 * /activity - the Sales Pipeline Funnel / Client Activity dashboard.
 *
 * One scored card per SHARED proposal (a client link exists). Score
 * is the backend's 0-100 Engagement Score mapped to HOT >= 65,
 * WARM 30-64, COLD < 30, NEVER = no client opens. Clicking a card
 * opens the per-email audit trail drill-down (who opened, dwell per
 * tab, downloads, scope edits, FX switches, forwards + a
 * chronological human-readable feed).
 */
import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Flame, Sparkles, Snowflake, MoonStar, Eye, Users, Download,
  SlidersHorizontal, Repeat, Share2, Search, Loader2,
  AlertTriangle, X, ExternalLink, ArrowDown, ArrowUp,
} from "lucide-react";
import StaffShell from "../components/staff/StaffShell";
import ActivityTrail from "../components/staff/ActivityTrail";
import ScoreExplainer from "../components/staff/ScoreExplainer";
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from "../components/ui/tooltip";

const API = process.env.REACT_APP_BACKEND_URL;

const SCORE_LABELS = {
  recency: "Recency",
  costs_dwell: "Time on costs",
  sample_changes: "Scope edits",
  fx_switches: "Currency switches",
  downloads: "Downloads",
  forwards: "Forwards",
  momentum: "Momentum",
};

function ScoreTooltip({ card, children }) {
  const rows = card.score_breakdown || [];
  return (
    <TooltipProvider delayDuration={150}>
      <Tooltip>
        <TooltipTrigger asChild>{children}</TooltipTrigger>
        <TooltipContent
          side="bottom"
          className="max-w-xs bg-slate-900 text-stone-100 text-xs leading-relaxed px-3 py-2.5"
          data-testid={`activity-score-tooltip-${card.token}`}
        >
          {card.temperature === "never" ? (
            <p>Not opened by the client yet - score 0/100. Staff previews never count.</p>
          ) : (
            <>
              <p className="font-semibold text-white mb-1.5">Why {card.score}/100?</p>
              <ul className="space-y-1">
                {rows.map((r, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <span className="font-mono text-emerald-400 shrink-0">+{r.pts}</span>
                    <span>{SCORE_LABELS[r.key] || r.key}: {r.detail}</span>
                  </li>
                ))}
              </ul>
              <p className="text-stone-400 mt-1.5">
                = {card.score}/100 (capped) · staff previews excluded
              </p>
            </>
          )}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

export const TEMP_META = {
  hot:   { label: "Hot",        icon: Flame,     pill: "bg-rose-100 text-rose-700 border-rose-200",   ring: "text-rose-600" },
  warm:  { label: "Warm",       icon: Sparkles,  pill: "bg-amber-100 text-amber-700 border-amber-200", ring: "text-amber-600" },
  cold:  { label: "Cold",       icon: Snowflake, pill: "bg-sky-100 text-sky-700 border-sky-200",       ring: "text-sky-600" },
  never: { label: "Not opened", icon: MoonStar,  pill: "bg-stone-100 text-stone-500 border-stone-200", ring: "text-stone-400" },
};

const fmtCurrency = (v) => {
  if (v == null || Number.isNaN(v)) return null;
  return `£${Math.round(v).toLocaleString("en-GB")}`;
};

export const timeAgo = (iso) => {
  if (!iso) return null;
  const ms = Date.now() - new Date(iso).getTime();
  if (Number.isNaN(ms)) return null;
  const mins = Math.floor(ms / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 30) return `${days}d ago`;
  return `${Math.floor(days / 30)}mo ago`;
};

const FILTERS = [
  { key: "all",        label: "All" },
  { key: "hot",        label: "Hot" },
  { key: "warm",       label: "Warm" },
  { key: "cold",       label: "Cold" },
  { key: "never",      label: "Not opened" },
  { key: "going_cold", label: "Going cold" },
];

// SharePoint lifecycle groups. "lost" also covers options that were
// not selected when a sibling execution was commissioned.
const LIFECYCLES = [
  { key: "open", label: "Open" },
  { key: "won",  label: "Won" },
  { key: "lost", label: "Lost / not selected" },
  { key: "all",  label: "Everything" },
];
const lifecycleGroup = (card) => {
  const st = card.lifecycle?.stage || "open";
  if (st === "won") return "won";
  if (st === "lost" || st === "superseded") return "lost";
  return "open";
};
const LIFECYCLE_BADGE = {
  won: "bg-emerald-100 text-emerald-700 border-emerald-200",
  lost: "bg-stone-100 text-stone-600 border-stone-200",
  superseded: "bg-stone-100 text-stone-500 border-stone-200",
};

function LifecycleBadge({ card }) {
  const lc = card.lifecycle;
  if (!lc || !lc.closed) return null;
  return (
    <span
      title={lc.note || `SharePoint status: ${lc.status}`}
      data-testid={`activity-lifecycle-${card.token}`}
      className={`ml-2 shrink-0 whitespace-nowrap text-[9px] font-semibold uppercase rounded px-1 py-px border ${LIFECYCLE_BADGE[lc.stage] || LIFECYCLE_BADGE.lost}`}
    >
      {lc.stage === "won" ? "Won" : lc.status || "Closed"}
    </span>
  );
}

const SORT_ACCESSORS = {
  score: (c) => c.score ?? 0,
  value: (c) => c.value_gbp ?? 0,
  expiry: (c) => c.days_to_expiry ?? 9999,
  last_open: (c) => c.stats?.last_open_at || "",
  sent: (c) => c.created_at || "",
};

function KpiTile({ tempKey, count, active, onClick }) {
  const meta = TEMP_META[tempKey] || {
    label: "Going cold", icon: AlertTriangle,
    pill: "bg-orange-100 text-orange-700 border-orange-200",
  };
  const Icon = meta.icon;
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={`activity-kpi-${tempKey}`}
      className={[
        "flex items-center gap-3 px-4 py-3 rounded-xl border bg-white text-left transition-all",
        active ? "border-[#1B4F8A] shadow-sm ring-1 ring-[#1B4F8A]/20" : "border-stone-200 hover:border-stone-300",
      ].join(" ")}
    >
      <span className={`inline-flex items-center justify-center w-8 h-8 rounded-lg border ${meta.pill}`}>
        <Icon className="w-4 h-4" />
      </span>
      <span>
        <span className="block text-xl font-semibold text-slate-900 leading-none">{count}</span>
        <span className="block text-[11px] text-stone-500 mt-1">{meta.label}</span>
      </span>
    </button>
  );
}

function MiniChip({ icon: Icon, v, title }) {
  return (
    <span
      title={title}
      className={`inline-flex items-center gap-0.5 text-[11px] font-mono ${v > 0 ? "text-slate-700" : "text-stone-300"}`}
    >
      <Icon className="w-3 h-3" />
      {v}
    </span>
  );
}

function SortTh({ label, k, sortKey, sortDir, onSort, align = "left", testid }) {
  const active = sortKey === k;
  const Arrow = sortDir === "asc" ? ArrowUp : ArrowDown;
  return (
    <th className={`px-3 py-2.5 font-medium text-${align} whitespace-nowrap`}>
      <button
        type="button"
        onClick={() => onSort(k)}
        data-testid={testid}
        className={`inline-flex items-center gap-1 uppercase tracking-wider transition-colors ${active ? "text-[#1B4F8A] font-semibold" : "text-stone-400 hover:text-stone-600"}`}
      >
        {label}
        <Arrow className={`w-3 h-3 ${active ? "opacity-100" : "opacity-30"}`} />
      </button>
    </th>
  );
}

function ExpiryChip({ days }) {
  if (days == null) return null;
  if (days < 0) {
    return (
      <span className="inline-flex items-center gap-1 text-[10px] font-mono px-1.5 py-0.5 rounded bg-rose-50 text-rose-700 border border-rose-200">
        quote expired {Math.abs(days)}d ago
      </span>
    );
  }
  const urgent = days <= 14;
  return (
    <span
      className={[
        "inline-flex items-center gap-1 text-[10px] font-mono px-1.5 py-0.5 rounded border",
        urgent ? "bg-amber-50 text-amber-700 border-amber-200" : "bg-stone-50 text-stone-500 border-stone-200",
      ].join(" ")}
    >
      {days}d validity left
    </span>
  );
}

function ActivityRow({ card, onOpen }) {
  const meta = TEMP_META[card.temperature] || TEMP_META.never;
  const Icon = meta.icon;
  const s = card.stats || {};
  const value = fmtCurrency(card.value_gbp);
  return (
    <tr
      onClick={() => onOpen(card)}
      onKeyDown={(e) => e.key === "Enter" && onOpen(card)}
      tabIndex={0}
      data-testid={`activity-card-${card.token}`}
      className="border-b border-stone-50 hover:bg-stone-50/60 transition-colors cursor-pointer"
    >
      {/* Client / project */}
      <td className="px-5 py-2.5 max-w-[300px]">
        <p className="font-medium text-slate-900 leading-snug flex items-center min-w-0" data-testid="activity-card-company">
          <span className="truncate">{card.client_company || "Unknown client"}</span>
          {card.bundle_size > 1 && (
            <span
              title={`Bundle options: ${(card.bundle_cids || []).join(", ")}`}
              className="ml-2 shrink-0 text-[9px] font-mono uppercase text-indigo-600 bg-indigo-50 border border-indigo-200 rounded px-1 py-px"
            >
              ×{card.bundle_size} options
            </span>
          )}
          {(card.revisions?.count || 0) > 0 && (
            <span
              title={(card.revisions.latest?.changes || []).join(" · ")}
              data-testid="activity-card-revision"
              className="ml-2 shrink-0 text-[9px] font-semibold uppercase text-violet-700 bg-violet-100 border border-violet-200 rounded px-1 py-px"
            >
              rev {card.revisions.count + 1}
            </span>
          )}
          {card.going_cold && (
            <span className="ml-2 shrink-0 inline-flex items-center gap-0.5 text-[9px] font-semibold uppercase px-1 py-px rounded bg-orange-100 text-orange-700 border border-orange-200">
              <AlertTriangle className="w-2.5 h-2.5" />
              going cold
            </span>
          )}
          <LifecycleBadge card={card} />
        </p>
        <p className="text-[11px] text-stone-500 truncate">
          {card.project_reference || card.project_number || "-"}
        </p>
        <p
          className="text-[10px] font-mono text-stone-400 mt-0.5"
          title={card.lifecycle?.note || undefined}
          data-testid={`activity-card-status-${card.token}`}
        >
          #{card.cid}
          {card.pipeline_status ? ` · ${card.pipeline_status}` : ""}
          {card.lifecycle?.source === "pending" ? " · awaiting SharePoint" : ""}
        </p>
      </td>
      {/* Value */}
      <td className="px-3 py-2.5 text-right whitespace-nowrap font-mono tabular-nums text-slate-700">
        <span data-testid="activity-card-value">{value || "-"}</span>
      </td>
      {/* Validity */}
      <td className="px-3 py-2.5 whitespace-nowrap">
        {card.lifecycle?.closed ? (
          <span className="text-[10px] font-mono text-stone-300">closed</span>
        ) : (
          <ExpiryChip days={card.days_to_expiry} />
        )}
      </td>
      {/* Score */}
      <td className="px-3 py-2.5 text-center whitespace-nowrap">
        <ScoreTooltip card={card}>
          <span className="inline-flex items-center gap-2 cursor-help">
            <span
              data-testid="activity-card-temp"
              className={`inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full border ${meta.pill}`}
            >
              <Icon className="w-3 h-3" />
              {meta.label}
            </span>
            {card.temperature !== "never" && (
              <span className={`text-[11px] font-mono font-semibold ${meta.ring}`}>
                {card.score}/100
              </span>
            )}
          </span>
        </ScoreTooltip>
      </td>
      {/* Engagement */}
      <td className="px-3 py-2.5 whitespace-nowrap">
        <span className="inline-flex items-center gap-2">
          <MiniChip icon={Eye} v={s.sessions || 0} title="client visits" />
          <MiniChip icon={Users} v={Math.max(s.unique_emails || 0, s.unique_visitors || 0)} title="people" />
          <MiniChip icon={Download} v={s.downloads || 0} title="downloads" />
          <MiniChip icon={SlidersHorizontal} v={s.sample_changes || 0} title="sample changes" />
          <MiniChip icon={Repeat} v={s.fx_switches || 0} title="currency switches" />
          <MiniChip icon={Share2} v={s.forwards || 0} title="forwards" />
        </span>
      </td>
      {/* Last opened */}
      <td className="px-3 py-2.5 text-right whitespace-nowrap text-[11px] text-stone-500">
        {s.last_open_at ? (
          timeAgo(s.last_open_at)
        ) : (s.staff_preview_sessions || 0) > 0 ? (
          <span
            className="text-stone-400"
            title="No client opens yet, but the link was checked by staff (staff previews never count towards engagement)"
            data-testid="staff-preview-hint"
          >
            staff preview only
          </span>
        ) : (
          <span className="text-stone-300">never</span>
        )}
      </td>
      {/* Sent */}
      <td className="px-3 py-2.5 text-right whitespace-nowrap text-[11px] text-stone-400">
        {timeAgo(card.created_at) || "-"}
      </td>
      {/* Quick-open links */}
      <td className="px-5 py-2.5 whitespace-nowrap text-right">
        <a
          href={`/p/${card.token}?as=client`}
          target="_blank"
          rel="noreferrer"
          onClick={(e) => e.stopPropagation()}
          data-testid="card-open-as-client"
          title="View as client"
          className="inline-flex items-center gap-1 text-[10px] font-semibold text-[#1B4F8A] hover:text-[#3EA8DC] transition-colors mr-3"
        >
          <ExternalLink className="w-3 h-3" />
          client
        </a>
        <a
          href={`/p/${card.token}?as=staff`}
          target="_blank"
          rel="noreferrer"
          onClick={(e) => e.stopPropagation()}
          data-testid="card-open-as-staff"
          title="View as us"
          className="inline-flex items-center gap-1 text-[10px] font-semibold text-stone-500 hover:text-stone-700 transition-colors"
        >
          <ExternalLink className="w-3 h-3" />
          us
        </a>
      </td>
    </tr>
  );
}

export default function Activity() {
  const [cards, setCards] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [lifecycleFilter, setLifecycleFilter] = useState("open");
  const [sortKey, setSortKey] = useState("score");
  const [sortDir, setSortDir] = useState("desc");
  const [q, setQ] = useState("");

  const toggleSort = (key) => {
    if (sortKey === key) {
      setSortDir((d) => (d === "desc" ? "asc" : "desc"));
    } else {
      setSortKey(key);
      setSortDir(key === "expiry" ? "asc" : "desc");
    }
  };
  const [selected, setSelected] = useState(null);
  const [trail, setTrail] = useState(null);
  const [trailLoading, setTrailLoading] = useState(false);
  const [updatedAt, setUpdatedAt] = useState(null);

  const load = async (background = false) => {
    if (!background) setLoading(true);
    try {
      const { data } = await axios.get(`${API}/api/activity/cards`);
      setCards(data.cards || []);
      setUpdatedAt(new Date());
    } catch (e) {
      if (!background) {
        toast.error("Could not load activity", {
          description: e?.response?.data?.detail || e.message,
        });
      }
    } finally {
      if (!background) setLoading(false);
    }
  };

  useEffect(() => {
    load();
    const id = setInterval(() => {
      if (!document.hidden) load(true);
    }, 30 * 60 * 1000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openTrail = async (card) => {
    setSelected(card);
    setTrail(null);
    setTrailLoading(true);
    try {
      const { data } = await axios.get(`${API}/api/activity/${card.token}/trail`);
      setTrail(data);
    } catch (e) {
      toast.error("Could not load the audit trail", {
        description: e?.response?.data?.detail || e.message,
      });
      setSelected(null);
    } finally {
      setTrailLoading(false);
    }
  };

  const lifecycleCounts = useMemo(() => {
    const c = { open: 0, won: 0, lost: 0, all: 0 };
    for (const card of cards || []) {
      c[lifecycleGroup(card)] += 1;
      c.all += 1;
    }
    return c;
  }, [cards]);

  const inLifecycle = useMemo(
    () =>
      (cards || []).filter(
        (c) => lifecycleFilter === "all" || lifecycleGroup(c) === lifecycleFilter
      ),
    [cards, lifecycleFilter]
  );

  const counts = useMemo(() => {
    const c = { hot: 0, warm: 0, cold: 0, never: 0, going_cold: 0 };
    for (const card of inLifecycle) {
      c[card.temperature] = (c[card.temperature] || 0) + 1;
      if (card.going_cold) c.going_cold += 1;
    }
    return c;
  }, [inLifecycle]);

  const visible = useMemo(() => {
    let list = [...inLifecycle];
    if (filter === "going_cold") list = list.filter((c) => c.going_cold);
    else if (filter !== "all") list = list.filter((c) => c.temperature === filter);
    const ql = q.trim().toLowerCase();
    if (ql) {
      list = list.filter((c) =>
        [
          c.client_company,
          c.project_reference,
          c.project_number,
          String(c.cid || ""),
          ...(c.bundle_cids || []).map(String),
        ]
          .filter(Boolean)
          .some((v) => String(v).toLowerCase().includes(ql))
      );
    }
    const acc = SORT_ACCESSORS[sortKey] || SORT_ACCESSORS.score;
    const dir = sortDir === "desc" ? -1 : 1;
    list.sort((a, b) => {
      const va = acc(a);
      const vb = acc(b);
      const cmp = typeof va === "number" && typeof vb === "number"
        ? va - vb
        : String(va).localeCompare(String(vb));
      return dir * cmp || (b.score - a.score);
    });
    return list;
  }, [inLifecycle, filter, q, sortKey, sortDir]);

  return (
    <StaffShell max="wide">
      <div data-testid="activity-page" className="flex flex-col gap-6">
        {/* Header */}
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-2xl font-semibold text-[#1B4F8A] tracking-tight">Client Activity</h1>
            <p className="text-sm text-stone-500 mt-1 max-w-2xl">
              Every shared proposal, scored 0-100 on real client behaviour -
              opens, time on Costs, scope edits, currency switches, downloads
              and forwards. Hover a score for its derivation; click a row for
              the per-person audit trail.
            </p>
            <div className="mt-2">
              <ScoreExplainer />
            </div>
          </div>
          <div
            data-testid="activity-live-indicator"
            className="flex items-center gap-2 text-[11px] text-stone-400 pt-1"
          >
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-60" />
              <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500" />
            </span>
            live · refreshes every 30 min
            {updatedAt && ` · updated ${updatedAt.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })}`}
          </div>
        </div>

        {/* Lifecycle (SharePoint) */}
        <div className="flex items-center gap-1 flex-wrap" data-testid="activity-lifecycle-filters">
          <span className="text-[10px] font-mono uppercase tracking-wider text-stone-400 mr-1">SharePoint status</span>
          {LIFECYCLES.map((f) => (
            <button
              key={f.key}
              type="button"
              onClick={() => setLifecycleFilter(f.key)}
              data-testid={`activity-lifecycle-${f.key}`}
              className={[
                "px-3 py-1.5 rounded-full text-xs font-medium border transition-colors",
                lifecycleFilter === f.key
                  ? "bg-slate-900 text-white border-slate-900"
                  : "bg-white text-slate-600 border-stone-200 hover:border-stone-300",
              ].join(" ")}
            >
              {f.label}
              <span className={`ml-1.5 font-mono ${lifecycleFilter === f.key ? "text-stone-300" : "text-stone-400"}`}>
                {lifecycleCounts[f.key] || 0}
              </span>
            </button>
          ))}
        </div>

        {/* KPI strip */}
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
          {["hot", "warm", "cold", "never", "going_cold"].map((k) => (
            <KpiTile
              key={k}
              tempKey={k}
              count={counts[k] || 0}
              active={filter === k}
              onClick={() => setFilter(filter === k ? "all" : k)}
            />
          ))}
        </div>

        {/* Controls */}
        <div className="flex items-center gap-3 flex-wrap">
          <div className="relative flex-1 min-w-[220px] max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search company, reference or #"
              data-testid="activity-search-input"
              className="w-full pl-9 pr-8 py-2 rounded-lg border border-stone-200 bg-white text-sm focus:outline-none focus:border-[#3EA8DC] focus:ring-1 focus:ring-[#3EA8DC]/30"
            />
            {q && (
              <button
                type="button"
                onClick={() => setQ("")}
                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-stone-400 hover:text-stone-600"
                aria-label="Clear search"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
          <div className="flex items-center gap-1 flex-wrap">
            {FILTERS.map((f) => (
              <button
                key={f.key}
                type="button"
                onClick={() => setFilter(f.key)}
                data-testid={`activity-filter-${f.key}`}
                className={[
                  "px-3 py-1.5 rounded-full text-xs font-medium border transition-colors",
                  filter === f.key
                    ? "bg-[#1B4F8A] text-white border-[#1B4F8A]"
                    : "bg-white text-slate-600 border-stone-200 hover:border-stone-300",
                ].join(" ")}
              >
                {f.label}
              </button>
            ))}
          </div>
        </div>

        {/* Cards */}
        {loading && !cards ? (
          <div className="flex items-center justify-center py-24 text-stone-400 gap-2">
            <Loader2 className="w-5 h-5 animate-spin" />
            <span className="text-sm">Scoring proposals…</span>
          </div>
        ) : visible.length === 0 ? (
          <div data-testid="activity-empty" className="text-center py-24 text-stone-400 text-sm">
            {lifecycleFilter === "open" && lifecycleCounts.open === 0 && lifecycleCounts.all > 0
              ? "No open proposals. Every shared proposal has closed in SharePoint, use the status chips above to see them."
              : "No shared proposals match this filter."}
          </div>
        ) : (
          <div className="bg-white border border-stone-200 rounded-2xl overflow-x-auto shadow-sm">
            <table className="w-full text-sm" data-testid="activity-table">
              <thead>
                <tr className="text-[10px] font-mono uppercase tracking-wider text-stone-400 border-b border-stone-100">
                  <th className="px-5 py-2.5 text-left font-medium">Client / project</th>
                  <SortTh label="Value" k="value" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} align="right" testid="activity-sort-value" />
                  <SortTh label="Validity" k="expiry" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} align="left" testid="activity-sort-expiry" />
                  <SortTh label="Score" k="score" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} align="center" testid="activity-sort-score" />
                  <th className="px-3 py-2.5 text-left font-medium">Engagement</th>
                  <SortTh label="Last opened" k="last_open" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} align="right" testid="activity-sort-recent" />
                  <SortTh label="Sent" k="sent" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} align="right" testid="activity-sort-sent" />
                  <th className="px-5 py-2.5 text-right font-medium">Open</th>
                </tr>
              </thead>
              <tbody>
                {visible.map((card) => (
                  <ActivityRow key={card.token} card={card} onOpen={openTrail} />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <ActivityTrail
        open={!!selected}
        onClose={() => { setSelected(null); setTrail(null); }}
        card={selected}
        trail={trail}
        loading={trailLoading}
      />
    </StaffShell>
  );
}
